import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FolderKanban, Plus } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { ProjectSummary } from "@/api/generated/ProjectSummary"
import { useProjects } from "@/api/queries/projects"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Switch } from "@/components/ui/switch"
import { OrchestratorStatusBadge, ProjectColorDot, ProjectStatusBadge } from "@/features/projects/badges"
import { ProjectMenuItems } from "@/features/projects/project-actions"
import { ProjectFormDialog } from "@/features/projects/project-form-dialog"
import { completion } from "@/features/projects/utils"
import { useTaskBoardEvents } from "@/features/tasks/use-task-events"
import { formatNumber } from "@/lib/format"

interface ProjectsSearch {
  archived?: boolean
}

export const Route = createFileRoute("/_app/tasks/projects/")({
  staticData: { titleKey: "nav.items.projects" },
  validateSearch: (s: Record<string, unknown>): ProjectsSearch => ({
    archived: s.archived === true || s.archived === "true" ? true : undefined,
  }),
  component: ProjectsPage,
})

function ProjectsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const projects = useProjects({ include_archived: !!search.archived })
  // projects (and their task counts) change from other tabs, agents and the orchestrator
  useTaskBoardEvents()
  const [dialog, setDialog] = useState<{ open: boolean; project?: ProjectSummary }>({ open: false })
  const open = (id: number) =>
    void navigate({ to: "/tasks/projects/$projectId", params: { projectId: String(id) } })

  const columns = useMemo(
    () =>
      createColumns<ProjectSummary>((c) => [
        c.accessor("name", {
          header: t("tasks.projects.columns.project"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="flex max-w-md min-w-48 items-start gap-2.5">
              <ProjectColorDot color={row.original.color} className="mt-1.5" />
              <div className="min-w-0">
                <div className="truncate font-medium">{row.original.name}</div>
                <div className="text-muted-foreground truncate text-xs">
                  {row.original.description || t("tasks.projects.noDescription")}
                </div>
              </div>
            </div>
          ),
        }),
        c.accessor("status", {
          header: t("tasks.projects.columns.status"),
          cell: ({ getValue }) => <ProjectStatusBadge status={getValue()} />,
        }),
        c.accessor("task_count", {
          header: t("tasks.projects.columns.tasks"),
          cell: ({ row }) => {
            const p = row.original
            const pct = completion(p.task_count, p.open_task_count)
            return (
              <div className="w-36 space-y-1">
                <div className="text-muted-foreground flex justify-between gap-2 text-xs tabular-nums">
                  <span>
                    {t("tasks.projects.openOfTotal", {
                      open: formatNumber(p.open_task_count),
                      total: formatNumber(p.task_count),
                    })}
                  </span>
                  <span>{pct}%</span>
                </div>
                <Progress value={pct} aria-label={t("tasks.projects.progress")} />
              </div>
            )
          },
        }),
        c.accessor("orchestrator_status", {
          header: t("tasks.projects.columns.orchestrator"),
          cell: ({ row }) => (
            <div className="flex min-w-0 flex-col gap-0.5">
              <OrchestratorStatusBadge
                status={row.original.orchestrator_status}
                enabled={row.original.orchestrator_enabled}
              />
              {row.original.orchestrator_enabled && row.original.orchestrator_agent_name && (
                <span className="text-muted-foreground max-w-40 truncate text-xs">
                  {row.original.orchestrator_agent_name}
                </span>
              )}
            </div>
          ),
        }),
        c.accessor("updated_at", {
          header: t("tasks.projects.columns.updated"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("tasks.projects.title")}
        description={t("tasks.projects.description")}
        icon={FolderKanban}
        actions={
          <Button onClick={() => setDialog({ open: true })} data-testid="new-project">
            <Plus />
            {t("tasks.projects.new")}
          </Button>
        }
      />
      <DataTable
        label={t("tasks.projects.title")}
        data={projects.data}
        columns={columns}
        getRowId={(p) => String(p.id)}
        loading={projects.isPending}
        error={projects.error}
        onRetry={() => void projects.refetch()}
        searchable
        searchPlaceholder={t("tasks.projects.search")}
        initialSorting={[{ id: "updated_at", desc: true }]}
        onRowClick={(p) => open(p.id)}
        toolbar={
          <div className="flex items-center gap-2">
            <Switch
              id="show-archived"
              size="sm"
              checked={!!search.archived}
              onCheckedChange={(v) =>
                void navigate({ search: (prev) => ({ ...prev, archived: v || undefined }), replace: true })
              }
            />
            <Label htmlFor="show-archived" className="text-muted-foreground text-xs font-normal">
              {t("tasks.projects.showArchived")}
            </Label>
          </div>
        }
        rowActions={(p) => (
          <ProjectMenuItems project={p} onEdit={() => setDialog({ open: true, project: p })} />
        )}
        emptyState={
          <EmptyState
            variant="plain"
            icon={FolderKanban}
            title={t("tasks.projects.empty.title")}
            description={t("tasks.projects.empty.description")}
            action={
              <Button size="sm" onClick={() => setDialog({ open: true })}>
                <Plus />
                {t("tasks.projects.new")}
              </Button>
            }
          />
        }
      />
      <ProjectFormDialog
        open={dialog.open}
        project={dialog.project}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        onCreated={(p) => open(p.id)}
      />
    </PageContainer>
  )
}
